/**
 * SessionStats — Cartes de statistiques PM et environnement
 * Extrait de SessionDashboard pour respecter la limite 500 lignes
 *
 * Métriques affichées (retours Dr. Van Erck — 3 avril 2026) :
 * - Moyenne, Max, P95 (95e percentile)
 * - % du temps au-dessus du seuil d'alerte
 * Ces deux métriques évitent que la moyenne masque des épisodes courts mais intenses
 *
 * 10.5.312 : médiane, et temps au-dessus des seuils d'avertissement et
 * d'alerte en durée et en part du temps mesuré. Toutes les valeurs viennent
 * de l'analyse de la page (periodAnalysis) : session stockée sans période,
 * session fenêtrée avec une période.
 *
 * 10.5.313 (D239) : cartes PM en 3 lignes. Badge = niveau de la moyenne
 * (classifyPm, comme la bordure) ; ligne 2 = max · P95 · médiane ; ligne 3 =
 * temps au-dessus du seuil d'avertissement, tri-couleur D3 et palier en mots,
 * décrite par la légende ; « Détails » = temps au-dessus du seuil d'alerte.
 * Température et humidité : badge classifyEnv (règle de l'ancien bloc
 * d'alertes). Seuils de référence en info-bulle. Nombres selon la langue.
 *
 * CO2 (D253, lot 2) : carte CO₂ seulement si la session (ou la période) a du
 * CO2 (hasCo2) : moyenne, max, P95, temps au-dessus de la valeur max
 * recommandée (jamais « seuil »), rappel de la référence et info-bulle sur le
 * lissage (chaque valeur est une moyenne par fenêtre de mesure PM). Sans CO2,
 * aucune carte : la page ne change pas.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTheme } from '../constants/theme';
import { semanticColors } from '../constants/semanticColors';
import { PM_THRESHOLDS, TEMP_THRESHOLDS as TEMP, HUMIDITY_THRESHOLDS as HUM } from '../constants/pmThresholds';
import { classifyPm } from '../constants/classifyPm';
import { classifyEnv } from '../constants/classifyEnv';
import { classifyCo2, CO2_LEVELS } from '../constants/classifyCo2';
import { classifyTimeAbove, TIME_ABOVE_PCT } from '../constants/classifyTimeAbove';
import { formatNumberLocale, numberLocale } from '../utils/formatters';
import { MIN_MEASURES_FOR_BEST } from './analysisRange';
import { buildPeriodAnalysis, hasCo2 } from './period/periodAnalysis';
import { isExternalSensor } from '../constants/sensorLabels';
import { formatMinutesAbove } from './period/periodText';
import InfoTip from './InfoTip';
import { StatCard, LevelBadge, pmAccent, pctAccent, levelAccent } from './_statsBlocks';

const LEVEL_KEYS = {
  bon: 'alerts.level.good',
  a_surveiller: 'alerts.level.watch',
  eleve: 'alerts.level.high',
  non_mesure: 'common.notMeasured',
};

// Badge de la carte CO₂ : moyenne sous la valeur max recommandée → « Bon »,
// au-dessus → « Élevé » (pas de palier intermédiaire, une seule référence).
const CO2_LEVEL = {
  [CO2_LEVELS.OK]: 'bon',
  [CO2_LEVELS.ABOVE_REFERENCE]: 'eleve',
  [CO2_LEVELS.NOT_MEASURED]: 'non_mesure',
};

// Valeur mesurée à 1 décimale (ou `decimals`), « — » si absente.
const fmt = (value, decimals = 1) => formatNumberLocale(value, decimals) ?? '—';
// Seuil : entier sans décimale, sinon une décimale.
const fmtThreshold = (value) => formatNumberLocale(value, Number.isInteger(value) ? 0 : 1);

// analysis absente (appel direct, sans page) : analyse de la session entière,
// mêmes valeurs qu'avant 10.5.312.
const SessionStats = ({ session: storedSession, readings: allReadings, darkMode, analysis }) => {
  const { t } = useTranslation();
  const theme = getTheme(darkMode);
  const resolved = useMemo(
    () => analysis || buildPeriodAnalysis(storedSession, allReadings, null),
    [analysis, storedSession, allReadings],
  );
  const { session, exposure, hiddenP95 } = resolved;
  const showCo2 = hasCo2(session, resolved.readings);

  const badge = (level, note = null) => {
    const label = t(LEVEL_KEYS[level]);
    const text = note
      ? t('session.stats.levelWithNote', { level: label, note: t(`alerts.note.${note}`).toLocaleLowerCase(numberLocale()) })
      : label;
    return <LevelBadge level={level} text={text} ariaLabel={t('session.stats.badgeAria', { level: text })} darkMode={darkMode} />;
  };

  const pmLines = (key, maxVal) => {
    const { p95, median, warning, alert } = exposure[key];
    const lines = [{
      // Période de moins de 30 mesures : P95 masqué, la mention le dit.
      text: hiddenP95.includes(key)
        ? t('session.stats.spreadP95Hidden', { max: fmt(maxVal), median: fmt(median), min: MIN_MEASURES_FOR_BEST })
        : t('session.stats.spread', { max: fmt(maxVal), p95: fmt(p95), median: fmt(median) }),
    }];
    if (warning.pct == null) return { lines, details: null };
    // D3 : tri-couleur et palier en mots sur le % au-dessus du seuil d'AVERTISSEMENT.
    lines.push({
      text: t('session.stats.timeAboveWarning', {
        pct: fmt(warning.pct), threshold: fmtThreshold(warning.threshold),
        duration: formatMinutesAbove(warning.minutes),
        level: t(`session.stats.timeAboveLevel.${classifyTimeAbove(warning.pct)}`),
      }),
      accent: pctAccent(warning.pct),
    });
    const details = {
      summary: t('session.stats.details'),
      lines: [{ text: t('session.stats.timeAboveAlert', {
        pct: fmt(alert.pct), threshold: fmtThreshold(alert.threshold), duration: formatMinutesAbove(alert.minutes),
      }) }],
    };
    return { lines, details };
  };

  const pmCard = (key, pm, avg, maxVal, notMeasuredBySensor = false) => {
    const { lines, details } = notMeasuredBySensor
      ? { lines: [{ text: t('sessions.csv.pm10NotMeasured') }], details: null }
      : pmLines(key, maxVal);
    return (
      <StatCard testId={`stat-card-${key}`} label={t('session.stats.pmAvg', { pm })}
        value={formatNumberLocale(avg, 1)} unit="µg/m³" badge={badge(classifyPm(avg, key).level)}
        lines={lines} details={details} accentColor={pmAccent(key, avg)} darkMode={darkMode} />
    );
  };

  const envCard = (kind, label, avg, min, max, decimals, unit, range) => {
    const { level, note } = classifyEnv(avg, kind);
    return (
      <StatCard testId={`stat-card-${kind}`} label={label} value={formatNumberLocale(avg, decimals)} unit={unit}
        badge={badge(level, note)} accentColor={levelAccent(level)} darkMode={darkMode}
        lines={[{ text: t('session.stats.normalRange', {
          min: fmt(min, decimals), max: fmt(max, decimals),
          lo: fmtThreshold(range.normalMin), hi: fmtThreshold(range.normalMax), unit,
        }) }]} />
    );
  };

  // Carte CO₂ : valeurs en ppm entiers ; P95 masqué sous 30 mesures comme les PM.
  const co2Card = () => {
    const { measured, p95, above } = exposure.co2;
    const level = CO2_LEVEL[classifyCo2(session.co2_avg)];
    const lines = [{
      text: measured < MIN_MEASURES_FOR_BEST
        ? t('session.stats.co2SpreadP95Hidden', { max: fmt(session.co2_max, 0), min: MIN_MEASURES_FOR_BEST })
        : t('session.stats.co2Spread', { max: fmt(session.co2_max, 0), p95: fmt(p95, 0) }),
    }];
    if (above.pct != null) {
      lines.push({
        text: t('session.stats.co2TimeAbove', {
          pct: fmt(above.pct), v: fmtThreshold(above.threshold), duration: formatMinutesAbove(above.minutes),
        }),
        accent: pctAccent(above.pct),
      });
    }
    lines.push({
      text: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {t('session.stats.co2Reference', { v: fmtThreshold(above.threshold) })}
          <InfoTip label={t('session.stats.co2Info')} darkMode={darkMode} testId="co2-info">
            {t('session.stats.co2Tooltip')}
          </InfoTip>
        </span>
      ),
    });
    return (
      <StatCard testId="stat-card-co2" label={t('session.stats.co2')} value={formatNumberLocale(session.co2_avg, 0)}
        unit="ppm" badge={badge(level)} lines={lines} accentColor={levelAccent(level)} darkMode={darkMode} />
    );
  };

  const pct = { low: fmtThreshold(TIME_ABOVE_PCT.watchFrom), high: fmtThreshold(TIME_ABOVE_PCT.highAbove) };
  const dot = (color) => ({
    display: 'inline-block', width: '8px', height: '8px',
    borderRadius: '50%', background: color, marginRight: '4px',
  });
  const pmReference = (pm, param) => t('alerts.reference.pmLine', {
    pm, w: fmtThreshold(PM_THRESHOLDS[param].warningMax), a: fmtThreshold(PM_THRESHOLDS[param].alertMax),
  });

  return (
    <section data-testid="session-stats" aria-label={t('session.stats.sectionLabel')}>
      {/* Légende : décrit la ligne 3 des cartes PM ; ⓘ = seuils de référence. */}
      <div style={{ fontSize: '11px', color: theme.textSecondary, marginBottom: '8px',
        display: 'flex', gap: '6px 14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontWeight: '600', color: theme.textPrimary }}>{t('session.stats.legendTitle')}</span>
          <InfoTip label={t('session.stats.thresholdsInfo')} darkMode={darkMode} testId="thresholds-info">
            <strong style={{ display: 'block', marginBottom: '4px' }}>{t('alerts.reference.title')}</strong>
            <span style={{ display: 'block' }}>{pmReference('PM1', 'pm1')}</span>
            <span style={{ display: 'block' }}>{pmReference('PM2.5', 'pm25')}</span>
            <span style={{ display: 'block' }}>{pmReference('PM10', 'pm10')}</span>
            <span style={{ display: 'block' }}>
              {t('alerts.reference.temp', { min: fmtThreshold(TEMP.normalMin), max: fmtThreshold(TEMP.normalMax) })}
            </span>
            <span style={{ display: 'block' }}>
              {t('alerts.reference.humidity', { min: fmtThreshold(HUM.normalMin), max: fmtThreshold(HUM.normalMax) })}
            </span>
          </InfoTip>
        </span>
        <span><span style={dot(semanticColors.greenSolid)} />{t('session.stats.legend.low', pct)}</span>
        <span><span style={dot(semanticColors.amberSolid)} />{t('session.stats.legend.mid', pct)}</span>
        <span><span style={dot(semanticColors.redSolid)} />{t('session.stats.legend.high', pct)}</span>
        <span>{t('session.stats.badgeLegend')}</span>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '12px', marginBottom: '20px',
      }}>
        {pmCard('pm1', 'PM1', session.pm1_avg, session.pm1_max)}
        {pmCard('pm25', 'PM2.5', session.pm2_5_avg, session.pm2_5_max)}
        {pmCard('pm10', 'PM10', session.pm10_avg, session.pm10_max, isExternalSensor(session))}
        {envCard('temperature', t('session.stats.temperature'), session.temp_avg,
          session.temp_min, session.temp_max, 1, '°C', TEMP)}
        {envCard('humidity', t('session.stats.humidity'), session.humidity_avg,
          session.humidity_min, session.humidity_max, 0, '%', HUM)}
        {showCo2 && co2Card()}
      </div>
    </section>
  );
};

export default SessionStats;
